import React, { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Play, Quote, Check, ChevronDown, Footprints, Compass, Zap, Headphones, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useBooking } from "../context/BookingContext";
import { SCHEDULER_URL } from "../data/content";
import { OptImg } from "@/components/OptImg";

/* ---------- Primitives ---------- */
export function OrangeButton({ children, onClick, className = "", as = "button", href, testid, ...rest }) {
  const base =
    "inline-flex items-center justify-center font-eyebrow text-xs sm:text-sm text-white bg-ember px-7 py-4 rounded-[8px] " +
    "transition-all duration-300 hover:shadow-[0_0_26px_rgba(232,168,124,0.55)] hover:-translate-y-0.5 active:translate-y-0 " +
    "tracking-[0.16em] cursor-pointer";
  if (as === "a") {
    return (
      <a href={href} data-testid={testid} className={`${base} ${className}`} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} data-testid={testid} className={`${base} ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function schedulerLink(utmContent) {
  if (!SCHEDULER_URL) return SCHEDULER_URL;
  const base = `${SCHEDULER_URL}?utm_source=website`;
  return utmContent ? `${base}&utm_content=${encodeURIComponent(utmContent)}` : base;
}

export function BookButton({ children = "BOOK CLARITY SESSION", interest = "Clarity Session", testid, className = "", utm }) {
  const { openBooking } = useBooking();
  if (SCHEDULER_URL) {
    return (
      <OrangeButton as="a" href={schedulerLink(utm)} target="_blank" rel="noreferrer" testid={testid} className={className}>
        {children}
      </OrangeButton>
    );
  }
  return (
    <OrangeButton onClick={() => openBooking(interest)} testid={testid} className={className}>
      {children}
    </OrangeButton>
  );
}

export function Eyebrow({ children, light = false, className = "", as: Tag = "p" }) {
  return <Tag className={`font-eyebrow ${light ? "text-ember" : "text-white"} text-xs sm:text-[13px] mb-5 ${className}`}>{children}</Tag>;
}

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Section({ children, className = "", id, bg = "bg-ink" }) {
  return (
    <section id={id} className={`${bg} px-6 sm:px-10 py-24 sm:py-32 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

/* ---------- Hero with full-width photo + dark overlay ---------- */
export function PhotoHero({ image, alt, eyebrow, title, subtext, cta, ctaInterest, children, minH = "min-h-[78vh]", imgPosition = "center", imgStyle = {}, titleSize = "text-4xl sm:text-5xl lg:text-6xl", subtextWidth = "max-w-xl", contentPad = "pb-20 sm:pb-28 pt-40", childrenClassName = "", gradientOverlay = "from-ink via-ink/75 to-ink/30", tintOverlay = "bg-ink/35", parallax = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  return (
    <header ref={ref} className={`relative ${minH} flex items-end overflow-hidden`}>
      <motion.div className="absolute inset-0" style={parallax ? { y } : undefined}>
        <OptImg sizes="100vw" loading="eager" fetchPriority="high" src={image} alt={alt} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: imgPosition, ...imgStyle }} />
      </motion.div>
      <div className={`absolute inset-0 bg-gradient-to-t ${gradientOverlay}`} />
      <div className={`absolute inset-0 ${tintOverlay}`} />
      <div className={`relative max-w-6xl mx-auto w-full px-6 sm:px-10 ${contentPad}`}>
        <Reveal>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h1 className={`font-display-bold ${titleSize} leading-[1.05] max-w-3xl`}>{title}</h1>
          {subtext && <p className={`mt-6 text-base sm:text-lg text-white ${subtextWidth}`}>{subtext}</p>}
          {cta && (
            <div className="mt-9">
              <BookButton interest={ctaInterest}>{cta}</BookButton>
            </div>
          )}
          {children && <div className={childrenClassName}>{children}</div>}
        </Reveal>
      </div>
    </header>
  );
}

/* ---------- Stats bar ---------- */
export function StatsBar({ stats }) {
  return (
    <div className="bg-ink px-6 sm:px-10 pt-8 sm:pt-10 pb-16 sm:pb-20">
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-10">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="text-center">
            <div className="font-stat text-4xl sm:text-5xl text-white">{s.value}</div>
            <div className="font-eyebrow text-white text-[11px] mt-3 leading-snug">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

/* ---------- Card shells ---------- */
export function Card({ children, className = "", id }) {
  return (
    <div id={id} className={`bg-surface rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.1)] ${className}`}>{children}</div>
  );
}

export function VideoCard({ name, note, thumb, youtubeId, audioUrl, testid, pull, zoom = 1, epNum, shareSlug }) {
  const [playing, setPlaying] = useState(false);
  const poster = thumb || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null);
  const isPodcast = !!audioUrl;
  const copyShare = async () => {
    const url = `${window.location.origin}/the-intuitive-leader-podcast${shareSlug ? `#${shareSlug}` : ""}`;
    const shareData = { title: `${note || name} — The Intuitive Leader Podcast`, text: `${name} on The Intuitive Leader Podcast`, url };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Episode link copied.");
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };
  return (
    <Card className={`overflow-hidden group flex flex-col ${isPodcast ? "transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(0,0,0,0.55)]" : ""}`}>
      <div className={`relative aspect-video overflow-hidden ${isPodcast ? "bg-black" : ""}`} data-testid={testid}>
        {playing && youtubeId ? (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            title={name}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          />
        ) : (
          <button
            type="button"
            onClick={() => youtubeId && setPlaying(true)}
            className={`absolute inset-0 w-full h-full ${isPodcast ? "flex items-center justify-center" : ""}`}
            aria-label={`Play ${name}`}
            data-testid={testid ? `${testid}-play` : undefined}
          >
            {poster ? (
              isPodcast ? (
                <div className="h-full aspect-[3/4] overflow-hidden">
                  <div className="w-full h-full transition-transform duration-700 group-hover:scale-105">
                    <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" loading="lazy" src={poster} alt={name} className="w-full h-full object-cover object-[center_22%]" style={{ filter: "saturate(0.85) contrast(1.02) brightness(1.02)", transform: `scale(${zoom})`, transformOrigin: "center 28%" }} />
                  </div>
                </div>
              ) : (
                <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" loading="lazy" src={poster} alt={name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              )
            ) : (
              <div className="w-full h-full bg-callout" />
            )}
            {!isPodcast && (
              <>
                <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent group-hover:from-ink/60 transition-colors" />
                <div className="absolute bottom-4 left-4">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 text-white text-sm transition-colors group-hover:bg-white/20">
                    <Play className="w-3.5 h-3.5 fill-white" /> Watch
                  </span>
                </div>
              </>
            )}
          </button>
        )}
        {isPodcast && epNum && (
          <span className="absolute top-3 left-3 z-10 font-eyebrow text-[10px] text-white [text-shadow:_0_1px_4px_rgb(0_0_0_/_75%)]" data-testid={testid ? `${testid}-num` : undefined}>
            Ep {epNum}
          </span>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        {isPodcast && name && <p className="font-eyebrow text-white/50 text-[11px] leading-tight">{name}</p>}
        {isPodcast ? (
          <p className="font-display text-lg text-white leading-tight mt-2">{note}</p>
        ) : (
          <p className="font-display text-lg text-white leading-tight">{name}</p>
        )}
        {!isPodcast && note && <p className="text-sm text-white/55 mt-1">{note}</p>}
        {pull && (
          <p className="text-[15px] text-white leading-relaxed mt-4">
            <span className="text-ember mr-1">&ldquo;</span>{pull}
          </p>
        )}
        {isPodcast && (
          <div className="flex items-center justify-between gap-4 mt-6">
            <div className="flex items-center gap-6">
              <a href={`https://youtu.be/${youtubeId}`} target="_blank" rel="noreferrer" data-testid={testid ? `${testid}-watch` : undefined} className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition-colors">
                <Play className="w-3.5 h-3.5 fill-current" /> Watch
              </a>
              <a href={audioUrl} target="_blank" rel="noreferrer" data-testid={testid ? `${testid}-listen` : undefined} className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition-colors">
                <Headphones className="w-3.5 h-3.5" /> Listen
              </a>
            </div>
            <button type="button" onClick={copyShare} data-testid={testid ? `${testid}-share` : undefined} aria-label={`Share ${note || name}`} className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition-colors">
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

export function GdprNote({ className = "" }) {
  return (
    <p className={`text-white/35 text-xs ${className}`} data-testid="gdpr-note">
      No spam. Unsubscribe anytime.{" "}
      <Link to="/privacy" className="underline underline-offset-2 hover:text-white/60 transition-colors">Privacy policy</Link>.
    </p>
  );
}

export function AudienceCard({ role, note }) {
  return (
    <Card className="p-8 h-full hover:-translate-y-1 transition-transform duration-300">
      <p className="font-display text-xl text-white leading-tight">{role}</p>
      <p className="text-white mt-3 text-[15px]">{note}</p>
    </Card>
  );
}

export function TestimonialCard({ quote, name, title, photo }) {
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <Card className="p-8 h-full flex flex-col">
      <Quote className="w-9 h-9 text-ember fill-ember mb-5" />
      <p className="text-white text-[15px] leading-relaxed flex-1">{quote}</p>
      <div className="flex items-center gap-4 mt-7">
        {photo ? (
          <OptImg sizes="96px" loading="lazy" src={photo} alt={name} className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <span className="w-14 h-14 rounded-full bg-callout flex items-center justify-center font-name text-white/80 text-lg">{initials}</span>
        )}
        <div>
          <p className="text-white font-name text-lg leading-tight">{name}</p>
          <p className="text-white/70 text-sm">{title}</p>
        </div>
      </div>
    </Card>
  );
}

/* ---------- Rolling quote ticker ---------- */
export function TestimonialCarousel({ items }) {
  return (
    <div className="mask-fade">
      <Marquee speed={22} gradient={false} pauseOnHover>
        {items.map((t, i) => (
          <div key={i} className="w-[330px] sm:w-[380px] mx-3 flex">
            <TestimonialCard {...t} />
          </div>
        ))}
      </Marquee>
    </div>
  );
}

export function QuoteTicker({ quotes }) {
  return (
    <div className="bg-ink py-14 mask-fade">
      <Marquee speed={45} gradient={false} pauseOnHover>
        {quotes.map((q, i) => (
          <span key={i} className="flex items-center">
            <span className={`font-heading text-xl sm:text-2xl px-10 ${i % 2 === 0 ? "text-cream" : "text-ember"}`}>
              {q}
            </span>
            <span className="text-ember/40 text-2xl">/</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export function LogoMarquee({ logos }) {
  return (
    <div className="mask-fade" data-testid="logo-marquee">
      <Marquee speed={35} gradient={false} pauseOnHover>
        {logos.map((l, i) => (
          <span
            key={i}
            className="font-display text-2xl sm:text-[26px] text-white/45 px-9 sm:px-14 whitespace-nowrap"
            data-testid={`logo-name-${l.name.toLowerCase()}`}
          >
            {l.name}
          </span>
        ))}
      </Marquee>
    </div>
  );
}

/* ---------- Pricing ---------- */
const PRICE_ICONS = { walk: Footprints, compass: Compass, bolt: Zap };
const CTA_STYLES = {
  light: { backgroundColor: "#E8A87C", color: "#080C12" },
  mid: { backgroundColor: "#D96B3C", color: "#ffffff" },
  full: { backgroundColor: "#CC4100", color: "#ffffff" },
};
const LABEL_COLOR = { light: "#E8A87C", mid: "#D96B3C", full: "#CC4100" };

export function PricingCard({ plan }) {
  const [open, setOpen] = useState(false);
  const Icon = PRICE_ICONS[plan.icon] || Compass;
  const bookUrl = `${SCHEDULER_URL}?utm_source=website&utm_campaign=pricing&utm_content=${encodeURIComponent(plan.name)}&a1=${encodeURIComponent(`I'm interested in the ${plan.name} plan.`)}`;
  return (
    <div
      className="rounded-2xl p-8 flex flex-col bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.1)] transition-transform duration-300 hover:-translate-y-1.5"
      style={plan.bordered ? { border: "1px solid #CC4100" } : undefined}
      data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon className="w-6 h-6 mb-4" style={{ color: LABEL_COLOR[plan.variant] || "#C9D2DC" }} />
      <p className="font-eyebrow text-xs sm:text-[13px] mb-2 text-white">{plan.eyebrow}</p>
      <h3 className="font-display text-2xl" style={{ color: LABEL_COLOR[plan.variant] || "#ffffff" }}>{plan.name}</h3>
      <p className="mt-3 text-white/80 text-[15px] leading-relaxed">{plan.body}</p>

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`pricing-expand-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
        className="mt-4 inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors self-start"
        aria-expanded={open}
      >
        More details
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <ul className="mt-4 space-y-2.5">
          {(plan.details || []).map((d) => (
            <li key={d} className="flex gap-2.5 text-white/90 text-[14px]">
              <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: LABEL_COLOR[plan.variant] }} />
              <span>{d}</span>
            </li>
          ))}
        </ul>
        {plan.notFor && (
          <p className="mt-4 text-white/80 text-[13px] leading-relaxed">{plan.notFor}</p>
        )}
      </motion.div>

      <div className="mt-7 flex items-baseline gap-3">
        <span className="font-display text-3xl text-white">{plan.price}</span>
        <span className="text-white/70 text-sm">/ {plan.cadence}</span>
      </div>
      {plan.subline ? (
        <p className="mt-1.5 text-white/80 text-[13px]">{plan.subline}</p>
      ) : plan.monthly && (
        <p className="mt-1.5 text-white/80 text-[13px]">or {plan.monthly}/mo · {plan.commit} commitment</p>
      )}
      <div className="mt-6 flex-1 flex items-end">
        <a
          href={bookUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
          style={CTA_STYLES[plan.variant] || CTA_STYLES.full}
          className="w-full inline-flex items-center justify-center font-eyebrow text-xs sm:text-sm px-7 py-4 rounded-[8px] tracking-[0.16em] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_26px_rgba(204,65,0,0.4)]"
        >
          {plan.cta}
        </a>
      </div>
    </div>
  );
}

/* ---------- FAQ ---------- */
function AnswerWithLinks({ text, links }) {
  if (!links || !links.length) return text;
  let parts = [text];
  links.forEach((lnk, li) => {
    const next = [];
    parts.forEach((part, pi) => {
      if (typeof part !== "string") { next.push(part); return; }
      const idx = part.indexOf(lnk.phrase);
      if (idx === -1) { next.push(part); return; }
      next.push(part.slice(0, idx));
      next.push(
        lnk.to ? (
          <Link key={`${li}-${pi}`} to={lnk.to} className="text-ember hover:underline">{lnk.phrase}</Link>
        ) : (
          <a key={`${li}-${pi}`} href={lnk.href} target="_blank" rel="noreferrer" className="text-ember hover:underline">{lnk.phrase}</a>
        )
      );
      next.push(part.slice(idx + lnk.phrase.length));
    });
    parts = next;
  });
  return <>{parts}</>;
}

export function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-6 text-left p-6 sm:p-7"
      >
        <span className={`faq-q font-display text-lg transition-colors duration-300 ${open ? "text-embersoft" : "text-white"}`}>
          {item.q}
        </span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 transition-all duration-300 ${open ? "rotate-180 text-embersoft" : "text-white/60"}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <p className="faq-a px-6 sm:px-7 pb-7 text-white text-[15px] leading-relaxed">
          {item.links ? <AnswerWithLinks text={item.a} links={item.links} /> : item.a}
        </p>
      </motion.div>
    </Card>
  );
}

export function SectionHeader({ eyebrow, title, sub, center, light, eyebrowAs }) {
  return (
    <Reveal className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      {eyebrow && <Eyebrow light={light} as={eyebrowAs}>{eyebrow}</Eyebrow>}
      {title && <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight">{title}</h2>}
      {sub && <p className="mt-5 text-white text-lg">{sub}</p>}
    </Reveal>
  );
}

export { Link };
